import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, UserCheck } from "lucide-react";
import { TravelRequestDetails } from "./TravelRequestDetails";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { useI18n } from "@/hooks/useI18n";
import { supabase } from "@/integrations/supabase/client";
import { ClientDetailSheet } from "@/lib/dashboard/clients/ClientDetailSheet";
import { AdvisorDetailSheet } from "@/lib/dashboard/advisors/AdvisorDetailSheet";

// Tipo union per supportare entrambi i modelli
type TravelRequestType = DashboardTravelRequest | TravelRequest;

interface Designer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface TravelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelRequest: TravelRequestType | null;
  onAccept?: (id: string) => Promise<boolean>;
  onDecline?: (id: string) => Promise<boolean>;
  onAssign?: (id: string, designerId: string) => Promise<boolean>;
  showActions?: boolean;
  isAdmin?: boolean;
  /** Se impostato (ruolo agency), limita la selezione ai soli membri di questa agenzia */
  agencyId?: string;
  /**
   * Rende cliccabili contatto e consulente: aprono le schede laterali con storico
   * viaggi, rate e incassi. Va acceso solo per i ruoli che le RPC
   * `get_client_detail` / `get_advisor_detail` ammettono -- agency, admin,
   * travel_designer, travel_advisor_member -- e mai nella proposta condivisa, dove
   * la stessa modale la vede il viaggiatore. Chi non puo' vedere una certa scheda
   * riceve comunque NULL dalla RPC, e il pannello dice "non trovata".
   */
  enableDetailSheets?: boolean;
}

export const TravelRequestModal = ({
  isOpen,
  onClose,
  travelRequest,
  onAccept,
  onDecline,
  onAssign,
  showActions = true,
  isAdmin = false,
  agencyId,
  enableDetailSheets = false,
}: TravelRequestModalProps) => {
  const { t } = useI18n();
  // `null` = pannello cliente chiuso. La chiave e' l'email normalizzata: e' la stessa
  // che la dashboard usa come client_key (lower(contact_email)).
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [clientPeriodOnly, setClientPeriodOnly] = useState(false);
  // Stessa storia per il consulente assegnato. I due pannelli non convivono: aprirne
  // uno chiude l'altro, come nella Panoramica.
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [advisorPeriodOnly, setAdvisorPeriodOnly] = useState(false);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [selectedDesignerId, setSelectedDesignerId] = useState("");
  const [isLoadingDesigners, setIsLoadingDesigners] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const canAssignDesigner = isAdmin || !!agencyId;

  useEffect(() => {
    if (!canAssignDesigner || !isOpen) return;
    setIsLoadingDesigners(true);

    const mapDesigners = (data: { id: string; first_name: string | null; last_name: string | null; email: string }[]) =>
      data.map((d) => ({
        id: d.id,
        firstName: d.first_name ?? "",
        lastName: d.last_name ?? "",
        email: d.email,
      }));

    const load = async () => {
      // get_my_advisors è role-aware lato DB (SECURITY DEFINER):
      // admin riceve tutti i travel_designer/travel_advisor_member approvati,
      // agency riceve solo i propri membri affiliati (via user_public_profile.agency_id).
      const { data, error } = await supabase.rpc("get_my_advisors" as any);
      if (!error && data) setDesigners(mapDesigners(data));
      else setDesigners([]);
      setIsLoadingDesigners(false);
    };

    load();
  }, [isAdmin, agencyId, canAssignDesigner, isOpen]);

  // Pre-seleziona il designer già assegnato (se presente), altrimenti quello preferito.
  // Solo se è davvero fra le opzioni: una richiesta assegnata all'agenzia stessa
  // porta un id che non compare tra i membri, e il Select mostrerebbe un valore fantasma.
  useEffect(() => {
    if (!travelRequest) return;
    const assignedId =
      "assignedAdvisorId" in travelRequest
        ? travelRequest.assignedAdvisorId
        : travelRequest.assigned_designer_id;
    const preferredId =
      "preferredAdvisorId" in travelRequest
        ? travelRequest.preferredAdvisorId
        : travelRequest.travel_designer_preference ?? null;
    const isSelectable = (id?: string | null) => !!id && designers.some((d) => d.id === id);
    setSelectedDesignerId(
      isSelectable(assignedId) ? assignedId! : isSelectable(preferredId) ? preferredId! : ""
    );
  }, [travelRequest, designers]);

  // Chiudendo la modale si chiude anche la scheda: riaprire una richiesta diversa con
  // il pannello del cliente precedente ancora aperto sarebbe un dato sbagliato.
  useEffect(() => {
    if (!isOpen) {
      setClientKey(null);
      setClientPeriodOnly(false);
      setAdvisorId(null);
      setAdvisorPeriodOnly(false);
    }
  }, [isOpen]);

  const preferredAdvisorId =
    travelRequest && "preferredAdvisorId" in travelRequest
      ? travelRequest.preferredAdvisorId
      : travelRequest && "travel_designer_preference" in travelRequest
        ? (travelRequest as any).travel_designer_preference ?? null
        : null;

  if (!travelRequest) return null;

  const assignedAdvisorId =
    "assignedAdvisorId" in travelRequest
      ? travelRequest.assignedAdvisorId
      : travelRequest.assigned_designer_id;
  const assignedAdvisorName =
    "assignedAdvisor" in travelRequest && travelRequest.assignedAdvisor
      ? travelRequest.assignedAdvisor.fullName
      : "users_detail" in travelRequest && travelRequest.users_detail?.assigned
        ? `${travelRequest.users_detail.assigned.first_name} ${travelRequest.users_detail.assigned.last_name}`.trim()
        : null;
  const assignedAdvisorEmail =
    "assignedAdvisor" in travelRequest && travelRequest.assignedAdvisor
      ? travelRequest.assignedAdvisor.email
      : ("users_detail" in travelRequest && travelRequest.users_detail?.assigned?.email) || null;

  const contactEmail =
    "contactEmail" in travelRequest ? travelRequest.contactEmail : travelRequest.contact_email;
  const contactFullName =
    "contactFullName" in travelRequest
      ? travelRequest.contactFullName
      : `${travelRequest.contact_first_name} ${travelRequest.contact_last_name}`.trim();

  // Lo stato sta nel titolo, accanto alla destinazione: e' la prima cosa da sapere
  // aprendo la richiesta, e il box del contatto deve parlare solo del contatto.
  // `pending` non ha chip suo: lo raccontano gia' il badge "nuova" qui accanto e
  // l'avviso di scadenza in fondo alla modale.
  const statusChip: Record<string, { label: string; className: string }> = {
    assigned: {
      label: String(t("travelRequest.statusAssigned")),
      className: "bg-gray-100 border-gray-300 text-gray-700",
    },
    completed: {
      label: String(t("travelRequest.statusCompleted")),
      className: "bg-emerald-50 border-emerald-200 text-emerald-700",
    },
    rejected: {
      label: String(t("travelRequest.statusRejected")),
      className: "bg-red-50 border-red-200 text-red-700",
    },
  };
  const currentStatusChip = statusChip[travelRequest.status];

  const getCreatedAt = () =>
    "createdAt" in travelRequest ? travelRequest.createdAt : travelRequest.created_at;

  const isNewRequest = (createdAt: string) => {
    const diffInHours =
      (new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  };

  const handleAccept = async () => {
    if (!onAccept) return;
    const success = await onAccept(travelRequest.id);
    if (success) onClose();
  };

  const handleDecline = async () => {
    if (!onDecline) return;
    const success = await onDecline(travelRequest.id);
    if (success) onClose();
  };

  const handleAssign = async () => {
    if (!onAssign || !selectedDesignerId) return;
    setIsAssigning(true);
    const success = await onAssign(travelRequest.id, selectedDesignerId);
    setIsAssigning(false);
    if (success) onClose();
  };

  const assignFooter = (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        <Select
          value={selectedDesignerId}
          onValueChange={setSelectedDesignerId}
          disabled={isLoadingDesigners}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t("travelRequest.selectAdvisor")} />
          </SelectTrigger>
          <SelectContent>
            {designers.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                <span className="flex items-center gap-2">
                  {d.firstName} {d.lastName} ({d.email})
                  {preferredAdvisorId && d.id === preferredAdvisorId && (
                    <Badge variant="secondary" className="text-sm ml-1">
                      {t("travelRequest.preferred")}
                    </Badge>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="default"
          onClick={handleAssign}
          disabled={!selectedDesignerId || isAssigning}
        >
          <UserCheck className="h-4 w-4" />
          {t("travelRequest.assign")}
        </Button>
      </div>
    </div>
  );

  const designerFooter = (
    <>
      <Button variant="outline" onClick={handleDecline}>
        <X className="h-4 w-4" /> {t("travelRequest.decline")}
      </Button>
      <Button variant="default" onClick={handleAccept}>
        <Check className="h-4 w-4" /> {t("travelRequest.accept")}
      </Button>
    </>
  );

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex flex-wrap items-center gap-2">
          <span>
            {t("travelRequest.title")}{" "}
            {travelRequest.destinations.length > 0
              ? travelRequest.destinations.join(", ")
              : t("travelRequest.destinationTBD")}
          </span>
          {currentStatusChip && (
            <span
              className={`flex-shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${currentStatusChip.className}`}
            >
              {currentStatusChip.label}
            </span>
          )}
          {isNewRequest(getCreatedAt()) && (
            <Badge variant="default" className="text-sm px-2 py-1 rounded-full">
              {t("common.new")}
            </Badge>
          )}
        </div>
      }
      size="lg"
      footer={showActions ? (canAssignDesigner ? assignFooter : designerFooter) : null}
    >
      <TravelRequestDetails
        travelRequest={travelRequest}
        onClientClick={
          enableDetailSheets && contactEmail
            ? () => {
                setAdvisorId(null);
                setClientKey(contactEmail.trim().toLowerCase());
              }
            : undefined
        }
        onAdvisorClick={
          enableDetailSheets && assignedAdvisorId
            ? () => {
                setClientKey(null);
                setAdvisorId(assignedAdvisorId);
              }
            : undefined
        }
      />
    </Modal>

    {/* Fuori dalla Modal: la scheda vive in un portale suo e deve poter stare sopra,
        non dentro al pannello che scorre. */}
    {enableDetailSheets && (
      <ClientDetailSheet
        clientKey={clientKey}
        onOpenChange={(open) => {
          if (!open) {
            setClientKey(null);
            setClientPeriodOnly(false);
          }
        }}
        period="30d"
        periodOnly={clientPeriodOnly}
        onPeriodOnlyChange={setClientPeriodOnly}
        fallbackName={contactFullName}
        fallbackEmail={contactEmail}
        aboveModal
      />
    )}

    {enableDetailSheets && (
      <AdvisorDetailSheet
        advisorId={advisorId}
        onOpenChange={(open) => {
          if (!open) {
            setAdvisorId(null);
            setAdvisorPeriodOnly(false);
          }
        }}
        period="30d"
        periodOnly={advisorPeriodOnly}
        onPeriodOnlyChange={setAdvisorPeriodOnly}
        fallbackName={assignedAdvisorName ?? undefined}
        fallbackEmail={assignedAdvisorEmail}
        // Dalla scheda del consulente si salta a quella di un suo cliente, come nella
        // Panoramica: il pannello si sostituisce, non se ne apre un terzo.
        onOpenClient={(key) => {
          setAdvisorId(null);
          setClientKey(key);
        }}
        aboveModal
      />
    )}
    </>
  );
};
